/**
 * The hat switcher (SPEC.md "One person, multiple hats"). A single, always
 * visible control in the top nav that flips the whole app between the account's
 * hats: My Projects, My Students, My Kids. Only hats the account actually has
 * appear, and an account with a single hat shows no switcher at all. Switching
 * is instant: it navigates to that hat's home (client-side, no reload) and the
 * shell re-scopes to it. The choice is remembered for next time by setHat.
 *
 * On a wide screen this is a segmented control with a label beside every icon.
 * On a phone the labels do not fit next to the logo and actions, so the segments
 * would collapse to bare, unreadable icons. Instead the phone layout shows one
 * compact button naming the current hat, which opens a plainly labeled menu of
 * all the hats. Either way every hat is identifiable by name, never icon alone.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Hat } from '@proteam/shared';

import { Icon } from '../ui/index.js';
import { useAuth } from '../lib/auth.js';
import { HAT_META, hatHome, shouldShowSwitcher } from './hats.js';

export function HatSwitcher() {
  const { hats, hat, setHat } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement>(null);

  // Close the phone menu on an outside tap or Escape, matching the account menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  if (!shouldShowSwitcher(hats) || !hat) return null;

  function pick(h: Hat) {
    setMenuOpen(false);
    if (h === hat) return;
    setHat(h);
    navigate(hatHome(h));
  }

  const activeMeta = HAT_META[hat];

  return (
    <div className="pt-hatswitch">
      {/* Wide screens: a segmented control, one labeled button per hat. */}
      <div className="pt-hatswitch__seg" role="group" aria-label="Switch your view">
        {hats.map((h) => {
          const meta = HAT_META[h];
          const active = h === hat;
          return (
            <button
              key={h}
              type="button"
              className={['pt-hatswitch__btn', active ? 'pt-hatswitch__btn--active' : '']
                .filter(Boolean)
                .join(' ')}
              aria-current={active ? 'true' : undefined}
              onClick={() => pick(h)}
            >
              <Icon name={meta.icon} size={18} />
              <span className="pt-hatswitch__label">{meta.label}</span>
            </button>
          );
        })}
      </div>

      {/* Phones: one compact button naming the current hat, opening a labeled menu. */}
      <div className="pt-hatswitch__mobile" ref={mobileRef}>
        <button
          type="button"
          className="pt-hatswitch__current"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`Your view: ${activeMeta.label}. Switch your view`}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <Icon name={activeMeta.icon} size={18} />
          <span className="pt-hatswitch__current-label">{activeMeta.label}</span>
          <Icon name="chevron-down" size={16} />
        </button>
        {menuOpen ? (
          <div className="pt-hatswitch__menu" role="menu" aria-label="Switch your view">
            {hats.map((h) => {
              const meta = HAT_META[h];
              const active = h === hat;
              return (
                <button
                  key={h}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  className={['pt-hatswitch__menuitem', active ? 'pt-hatswitch__menuitem--active' : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => pick(h)}
                >
                  <Icon name={meta.icon} size={18} />
                  <span className="pt-hatswitch__menuitem-label">{meta.label}</span>
                  {active ? <Icon name="check" size={16} /> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
